/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import {
	Box,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
} from '@mui/material';
import Close from '@mui/icons-material/Close';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';

import Button from '../button/button';
import { DynamicInput } from '../form-control';
import Modal from '../modal/modal';
import ModalLoader from '../loaders/modal-loader';
import Typography from '../typography/typography';

interface SourceTypeAddModalProps {
	isOpen: boolean;
	setIsOpen: (isOpen?: boolean) => void;
	isLoading?: boolean;
	onSave: (data: any) => Promise<void> | void;
	modalProps?: any;
}

interface SourceTypeFormValues {
	source_type: {
		name: string;
		description: string;
	};
}

const validationSchema = Yup.object().shape({
	source_type: Yup.object().shape({
		name: Yup.string()
			.trim()
			.required('common.source_type.source_type_name_required')
			.max(255, 'common.source_type.source_type_name_max'),
		description: Yup.string()
			.nullable()
			.max(1000, 'common.source_type.description_max'),
	}),
});

const defaultValues: SourceTypeFormValues = {
	source_type: {
		name: '',
		description: '',
	},
};

const SourceTypeAddModal: React.FC<SourceTypeAddModalProps> = ({
	isOpen,
	setIsOpen,
	isLoading = false,
	onSave,
	modalProps,
}) => {
	const { t } = useTranslation();
	const [isSubmitting, setIsSubmitting] = useState(false);

	const { control, handleSubmit, reset } = useForm<SourceTypeFormValues>({
		mode: 'all',
		resolver: yupResolver(validationSchema),
		defaultValues,
	});

	const handleClose = () => {
		reset(defaultValues);
		setIsOpen(false);
	};

	const onSubmit = async (values: SourceTypeFormValues) => {
		setIsSubmitting(true);
		try {
			await onSave({
				...values.source_type,
				description: values.source_type.description?.trim() || null,
			});
			handleClose();
		} catch (error) {
			console.error(t('common.source_type.error_creating_source_type'), error);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleSubmitForm = (e: React.FormEvent<HTMLFormElement>) => {
		e.stopPropagation();
		e.preventDefault();
		handleSubmit(onSubmit)(e);
	};

	return (
		<Modal
			open={isOpen}
			onClose={handleClose}
			maxWidth={modalProps?.maxWidth || 'sm'}
			fullWidth={modalProps?.fullWidth ?? true}
			{...modalProps}
		>
			<form onSubmit={handleSubmitForm}>
				{(isSubmitting || isLoading) && <ModalLoader />}
				<DialogTitle className="brandAddModal--DialogTitle">
					<Typography type="s3" color="inherit" weight="medium">
						{t('common.source_type.create_new_source_type')}
					</Typography>
					<IconButton onClick={(e) => {
						e.stopPropagation();
						handleClose();
					}}>
						<Close fontSize="small" />
					</IconButton>
				</DialogTitle>
				<DialogContent dividers>
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<DynamicInput
								label={t('common.source_type.source_type_name')}
								name="name"
								fieldArrayName="source_type"
								placeholder={t('common.source_type.enter_source_type_name')}
								formControl={control}
								required
							/>
						</Grid>
						<Grid item xs={12}>
							<DynamicInput
								label={t('common.source_type.description')}
								name="description"
								fieldArrayName="source_type"
								placeholder={t('common.source_type.enter_description_optional')}
								formControl={control}
								is_multiline
							/>
						</Grid>
					</Grid>
				</DialogContent>
				<DialogActions>
					<Box display="flex" gap={1.5} width="100%" justifyContent="flex-end">
						<Button
							variant="outlined"
							onClick={(e) => {
								e.stopPropagation();
								handleClose();
							}}
							disabled={isSubmitting || isLoading}
						>
							{t('common.cancel')}
						</Button>
						<Button
							variant="contained"
							type="submit"
							onClick={(e) => e.stopPropagation()}
							loading={isSubmitting || isLoading}
						>
							{t('common.source_type.create_source_type')}
						</Button>
					</Box>
				</DialogActions>
			</form>
		</Modal>
	);
};

export { SourceTypeAddModal };
